// import { createSlice, PayloadAction } from "@reduxjs/toolkit";
// import BASE_URL from "../api";
// import { AppDispatch, RootState } from "../../../store/store";
// import { Notification, NotificationState } from "../types/notification";

// const initialState: NotificationState = {
//     notifications: [],
//     loading: false,
//     unreadCount: 0,
// };

// const notificationSlice = createSlice({
//     name: "notification",
//     initialState,
//     reducers: {
//         start(state) {
//             state.loading = true;
//         },

//         fail(state) {
//             state.loading = false;
//         },

//         setNotifications(state, action: PayloadAction<Notification[]>) {
//             state.notifications = action.payload;
//             state.loading = false;
//             state.unreadCount = action.payload.filter(n => !n.is_read).length;
//         },

//         addNotification(state, action: PayloadAction<Notification>) {
//             state.notifications.unshift(action.payload);
//             if (!action.payload.is_read) state.unreadCount += 1;
//         },

//         markAllAsRead(state) {
//             state.notifications.forEach(n => (n.is_read = true));
//             state.unreadCount = 0;
//         },
//         markAsRead(state, action: PayloadAction<string>) {
//             const index = state.notifications.findIndex(
//                 (i) => i.id === action.payload
//             );

//             if (index !== -1 && !state.notifications[index].is_read) {
//                 state.notifications[index] = {
//                     ...state.notifications[index],
//                     is_read: true,
//                 };

//                 state.unreadCount = state.notifications.filter(
//                     (n) => !n.is_read
//                 ).length;
//             }
//         }
//     },
// });

// export const {
//     start,
//     fail,
//     setNotifications,
//     addNotification,
//     markAsRead,
//     markAllAsRead,
// } = notificationSlice.actions;

// export default notificationSlice.reducer;



// export const fetchNotifications =
//     () => async (dispatch: AppDispatch, getState: () => RootState) => {
//         dispatch(start());

//         try {
//             const token = getState().auth.access_token;

//             const res = await fetch(`${BASE_URL}/user/notifications`, {
//                 headers: {
//                     Authorization: `Bearer ${token}`,
//                     Accept: "application/json",
//                 },
//             });

//             const data = await res.json();

//             dispatch(setNotifications(data.data || []));
//         } catch (err) {
//             dispatch(fail());
//         }
//     };


// export const markNotificationAsRead =
//     (id: string) =>
//         async (dispatch: AppDispatch, getState: () => RootState) => {
//             const token = getState().auth.access_token;

//             await fetch(`${BASE_URL}/user/notifications/${id}/read`, {
//                 method: "PATCH",
//                 headers: {
//                     Authorization: `Bearer ${token}`,
//                     Accept: "application/json",
//                 },
//             });

//             dispatch(markAsRead(id));

//             //  OPTIONAL but IMPORTANT: sync with backend
//             dispatch(fetchNotifications());
//         };


// export type { NotificationState };



// File: src/store/reducers/slices/notificationSlice.ts

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import BASE_URL from "../api";
import { AppDispatch, RootState } from "../../../store/store";
import {
  Notification,
  NotificationState,
} from "../types/notification";

type NotificationMode = "user" | "admin";

interface ExtendedNotificationState extends NotificationState {
  mode: NotificationMode;
  error: string | null;
  totalCount: number;
}

interface PaginatedNotifications<T> {
  data: T[];
  total?: number;
  current_page?: number;
  last_page?: number;
  per_page?: number;
}

interface NotificationApiResponse {
  success?: boolean;
  message?: string;
  count?: number;
  unread_count?: number;
  data?: Notification[] | PaginatedNotifications<Notification>;
}

const initialState: ExtendedNotificationState = {
  notifications: [],
  loading: false,
  unreadCount: 0,
  mode: "user",
  error: null,
  totalCount: 0,
};

/**
 * Retrieve the token used by the admin authentication guard.
 *
 * Update the Redux property or localStorage key here if your
 * admin login stores its token somewhere else.
 */
const getAdminToken = (state: RootState): string | null => {
  const adminState = (
    state as RootState & {
      admin?: {
        access_token?: string;
        token?: string;
      };
    }
  ).admin;

  if (adminState?.access_token) {
    return adminState.access_token;
  }

  if (adminState?.token) {
    return adminState.token;
  }

  if (typeof localStorage !== "undefined") {
    return (
      localStorage.getItem("admin_token") ||
      localStorage.getItem("admin_access_token")
    );
  }

  return null;
};

/**
 * Select the correct authentication token for the requested feed.
 */
const getToken = (
  state: RootState,
  mode: NotificationMode
): string | null => {
  if (mode === "admin") {
    return getAdminToken(state);
  }

  return state.auth.access_token || null;
};

/**
 * Shared API headers.
 */
const getAuthHeaders = (token: string): HeadersInit => ({
  Authorization: `Bearer ${token}`,
  Accept: "application/json",
  "Content-Type": "application/json",
});

/**
 * Shared API request helper.
 */
const requestNotificationsApi = async (
  url: string,
  token: string,
  method: "GET" | "PATCH" = "GET"
): Promise<NotificationApiResponse> => {
  const response = await fetch(url, {
    method,
    headers: getAuthHeaders(token),
  });

  const data = (await response.json()) as NotificationApiResponse;

  if (!response.ok || data.success === false) {
    throw new Error(
      data.message ||
        `Notification request failed with status ${response.status}.`
    );
  }

  return data;
};

const notificationSlice = createSlice({
  name: "notification",
  initialState,

  reducers: {
    start(state) {
      state.loading = true;
      state.error = null;
    },

    fail(state, action: PayloadAction<string | undefined>) {
      state.loading = false;
      state.error =
        action.payload ?? "Failed to process notifications.";
    },

    setNotificationMode(
      state,
      action: PayloadAction<NotificationMode>
    ) {
      if (state.mode !== action.payload) {
        state.notifications = [];
        state.unreadCount = 0;
        state.totalCount = 0;
        state.error = null;
      }

      state.mode = action.payload;
    },

    setNotifications(
      state,
      action: PayloadAction<{
        notifications: Notification[];
        totalCount?: number;
        unreadCount?: number;
      }>
    ) {
      state.notifications = action.payload.notifications;
      state.totalCount =
        action.payload.totalCount ??
        action.payload.notifications.length;

      state.unreadCount =
        action.payload.unreadCount ??
        action.payload.notifications.filter(
          (notification) => !notification.is_read
        ).length;

      state.loading = false;
      state.error = null;
    },

    addNotification(
      state,
      action: PayloadAction<Notification>
    ) {
      const exists = state.notifications.some(
        (notification) =>
          String(notification.id) === String(action.payload.id)
      );

      if (exists) return;

      state.notifications.unshift(action.payload);
      state.totalCount += 1;

      if (!action.payload.is_read) {
        state.unreadCount += 1;
      }
    },

    markAsRead(state, action: PayloadAction<string>) {
      const notification = state.notifications.find(
        (item) => String(item.id) === action.payload
      );

      if (!notification || notification.is_read) return;

      notification.is_read = true;

      state.unreadCount = state.notifications.filter(
        (item) => !item.is_read
      ).length;
    },

    markAllAsRead(state) {
      state.notifications.forEach((notification) => {
        notification.is_read = true;
      });

      state.unreadCount = 0;
    },
  },
});

export const {
  start,
  fail,
  setNotificationMode,
  setNotifications,
  addNotification,
  markAsRead,
  markAllAsRead,
} = notificationSlice.actions;

export default notificationSlice.reducer;

export type { NotificationState };

/**
 * Fetch notifications for the selected authentication mode.
 *
 * User:  GET /user/notifications
 * Admin: GET /admin/notifications
 */
const fetchNotificationsForMode =
  (mode: NotificationMode) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setNotificationMode(mode));
    dispatch(start());

    try {
      const token = getToken(getState(), mode);

      if (!token) {
        throw new Error(
          mode === "admin"
            ? "Admin authentication token is missing. Please log in again."
            : "Authentication token is missing. Please log in again."
        );
      }

      const endpoint =
        mode === "admin"
          ? `${BASE_URL}/admin/notifications`
          : `${BASE_URL}/user/notifications`;

      const result = await requestNotificationsApi(endpoint, token);

      const responseData = result.data;

      const isPaginated =
        !Array.isArray(responseData) &&
        responseData !== undefined &&
        Array.isArray(responseData.data);

      const notifications: Notification[] = Array.isArray(responseData)
        ? responseData
        : isPaginated
          ? responseData.data
          : [];

      const totalCount = Array.isArray(responseData)
        ? notifications.length
        : isPaginated
          ? responseData.total ?? result.count ?? notifications.length
          : result.count ?? 0;

      const unreadCount =
        mode === "admin" && result.unread_count !== undefined
          ? result.unread_count
          : notifications.filter(
              (notification) => !notification.is_read
            ).length;

      dispatch(
        setNotifications({
          notifications,
          totalCount,
          unreadCount,
        })
      );
    } catch (error) {
      dispatch(
        fail(
          error instanceof Error
            ? error.message
            : "Unable to fetch notifications."
        )
      );
    }
  };

/**
 * Fetch the logged-in user's notifications.
 */
export const fetchNotifications = () =>
  fetchNotificationsForMode("user");

/**
 * Fetch notifications visible to an authenticated admin.
 */
export const fetchAdminNotifications = () =>
  fetchNotificationsForMode("admin");

/**
 * Mark one notification as read.
 */
export const markNotificationAsRead =
  (id: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const mode = state.notification.mode;
    const token = getToken(state, mode);

    if (!token) {
      dispatch(
        fail(
          mode === "admin"
            ? "Admin authentication token is missing. Please log in again."
            : "Authentication token is missing. Please log in again."
        )
      );
      return;
    }

    const endpoint =
      mode === "admin"
        ? `${BASE_URL}/admin/notifications/${encodeURIComponent(id)}/read`
        : `${BASE_URL}/user/notifications/${encodeURIComponent(id)}/read`;

    try {
      await requestNotificationsApi(endpoint, token, "PATCH");

      dispatch(markAsRead(id));
    } catch (error) {
      dispatch(
        fail(
          error instanceof Error
            ? error.message
            : "Unable to mark notification as read."
        )
      );
    }
  };

/**
 * Mark all notifications as read for the current mode.
 */
export const markAllNotificationsAsRead =
  () =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const mode = state.notification.mode;
    const token = getToken(state, mode);

    if (!token) {
      dispatch(
        fail(
          mode === "admin"
            ? "Admin authentication token is missing. Please log in again."
            : "Authentication token is missing. Please log in again."
        )
      );
      return;
    }

    const endpoint =
      mode === "admin"
        ? `${BASE_URL}/admin/notifications/read-all`
        : `${BASE_URL}/user/notifications/read-all`;

    try {
      await requestNotificationsApi(endpoint, token, "PATCH");

      dispatch(markAllAsRead());
    } catch (error) {
      dispatch(
        fail(
          error instanceof Error
            ? error.message
            : "Unable to mark all notifications as read."
        )
      );
    }
  };
